import { useCallback } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CheckIcon,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import templateUpgradeArt from './assets/template-upgrade-illustration.svg'

type CtaProperties = {
  view: string
  location: string
  button: string
}

type PostTemplatesPaywallDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  ctaProperties: CtaProperties
  upgradePathName: string
}

const PAYWALL_COPY = {
  heading: 'Upgrade to create more templates',
  body: "You've reached your plan's limit for post templates. Upgrade to save more of your favorite formats and reuse them across channels.",
  bulletPoints: [
    'More custom templates',
    'Share templates with your team',
    'Faster drafting from a growing library',
    'Advanced publishing and planning features',
  ],
  ctaText: 'See Upgrade Options',
}

export const PostTemplatesPaywallDialog = ({
  open,
  onOpenChange,
  ctaProperties,
  upgradePathName,
}: PostTemplatesPaywallDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const openGlobalModal = useOpenGlobalModal()
  const { view, location, button } = ctaProperties
  const cta = `publish-${view}-${location}-${button}-1` as const

  const handleUpgradeClick = (): void => {
    BufferTracker.cTAClicked({
      clientName: Client.PublishWeb,
      product: 'publish',
      cta,
      upgradePathName,
      organizationId: organizationId ?? '',
    })
    onOpenChange(false)
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        BufferTracker.upgradePathViewed({
          clientName: Client.PublishWeb,
          product: 'publish',
          organizationId: organizationId ?? '',
          upgradePathName,
          cta,
        })
      }
      onOpenChange(next)
    },
    [onOpenChange, cta, upgradePathName, organizationId],
  )

  return (
    <UpgradeDialog open={open} onOpenChange={handleOpenChange}>
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading as="h2" size="lg">
            {PAYWALL_COPY.heading}
          </Heading>
          <Text>{PAYWALL_COPY.body}</Text>
          <List>
            {PAYWALL_COPY.bulletPoints.map((item) => (
              <List.Item key={item} icon={CheckIcon}>
                {item}
              </List.Item>
            ))}
          </List>
          <Button variant="primary" size="large" onClick={handleUpgradeClick}>
            {PAYWALL_COPY.ctaText}
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src={templateUpgradeArt}
            alt=""
            placeholder={templateUpgradeArt}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
